import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpRequest, HttpStatusCode} from '@angular/common/http';
import * as Sentry from '@sentry/angular-ivy';
import {Repository} from '@clavisco/core';
import {environment} from '@Environment/environment';
import {StorageKey} from '@app/enums/e-storage-keys';
import {IUserToken} from '@app/interfaces/i-token';
import {ICompany} from '@app/interfaces/i-company';
import {IHeadquarter} from '@app/interfaces/i-headquarter';
import {IUserAssign} from '@app/interfaces/i-user';
import {ICurrentSession} from '@app/interfaces/i-localStorage';

/**
 * Publica en Sentry quien esta usando la aplicacion.
 *
 * Sin esto los issues y los replays llegan como usuarios anonimos y no hay forma
 * de saber a que usuario, compania o sede pertenece la sesion que se esta viendo.
 */
@Injectable({
  providedIn: 'root'
})
export class SentryService {

  /**
   * Lee la sesion del localstorage y la publica en Sentry.
   *
   * Es idempotente, por lo que se puede invocar cada vez que cambia algo del
   * contexto (login, recarga de la pagina, seleccion de compania o de sede) para
   * refrescar los datos que acompanan a los errores y a los replays.
   */
  SyncContext(): void {
    if (!environment.sentry.enabled) return;

    const session = Repository.Behavior.GetStorageObject<IUserToken>(StorageKey.Session);

    if (!session?.access_token) {
      this.ClearContext();
      return;
    }

    const company = Repository.Behavior.GetStorageObject<ICompany>(StorageKey.CurrentCompany);
    const headquarter = Repository.Behavior.GetStorageObject<IHeadquarter>(StorageKey.CurrentHeadquarter);
    const userAssign = Repository.Behavior.GetStorageObject<IUserAssign>(StorageKey.CurrentUserAssign);
    const currentSession = Repository.Behavior.GetStorageObject<ICurrentSession>(StorageKey.CurrentSession);

    // Es lo que Sentry muestra como "User" en el issue y en el reproductor de replays
    Sentry.setUser({
      id: `${session.UserId || ''}`,
      email: session.UserEmail,
      username: session.UserEmail
    });

    // Los tags son los unicos campos por los que se puede filtrar y buscar
    Sentry.setTag('company', company?.Name || SentryService.UNSET);
    Sentry.setTag('database', company?.DatabaseCode || SentryService.UNSET);
    Sentry.setTag('headquarter', headquarter?.Name || SentryService.UNSET);
    Sentry.setTag('warehouse', currentSession?.WhsCode || headquarter?.WhsCode || SentryService.UNSET);

    // El contexto no es filtrable pero se ve completo en el detalle del issue
    Sentry.setContext('Sesion EMA', {
      UserId: session.UserId,
      UserEmail: session.UserEmail,
      CompanyId: company?.Id,
      Company: company?.Name,
      DatabaseCode: company?.DatabaseCode,
      HeadquarterId: headquarter?.Id,
      Headquarter: headquarter?.Name,
      SlpCode: userAssign?.SlpCode,
      CenterCost: userAssign?.CenterCost,
      WhsCode: currentSession?.WhsCode,
      WhsName: currentSession?.WhsName,
      TokenExpira: session['.expires']
    });
  }

  /**
   * Borra el usuario y los tags de la sesion. Se invoca en el logout para que lo
   * que ocurra despues no quede atribuido al usuario anterior.
   */
  ClearContext(): void {
    if (!environment.sentry.enabled) return;

    Sentry.setUser(null);
    Sentry.setTag('company', undefined);
    Sentry.setTag('database', undefined);
    Sentry.setTag('headquarter', undefined);
    Sentry.setTag('warehouse', undefined);
    Sentry.setContext('Sesion EMA', null);
  }

  /**
   * Reporta a Sentry un error HTTP que la aplicacion ya manejo mostrando un toast
   * o un modal.
   *
   * Estos errores nunca llegan al ErrorHandler de Angular porque el subscriptor los
   * consume al mostrar la alerta, asi que sin esto Sentry no se enteraria de la
   * mayoria de las fallas reales: los rechazos de SAP, los timeouts y los 500 del API.
   *
   * Se adjunta lo que se envio y lo que respondio el API, que es lo que hace falta
   * para diagnosticar sin tener que reproducir el caso.
   */
  CaptureHttpError(_request: HttpRequest<unknown>, _response: HttpErrorResponse): void {
    if (!environment.sentry.enabled) return;

    if (!_request || !_response || _response.status === undefined) return;

    // La sesion vencida ya se maneja con su propio modal, no es una falla que diagnosticar
    if (_response.status === HttpStatusCode.Unauthorized) return;

    const isApi = _request.url.includes(environment.apiUrl);

    // Estado 0 fuera del API es la impresora local o el pinpad apagados: puro ruido
    if (_response.status === 0 && !isApi) return;

    const endpoint = SentryService.Endpoint(_request.url);

    Sentry.withScope(scope => {
      // Agrupa un issue por endpoint y codigo, en vez de uno por cada usuario que lo golpea
      scope.setFingerprint(['http', _request.method, endpoint, `${_response.status}`]);
      scope.setLevel(_response.status >= 500 || _response.status === 0 ? 'error' : 'warning');
      scope.setTag('http.method', _request.method);
      scope.setTag('http.status', `${_response.status}`);
      scope.setTag('http.endpoint', endpoint);

      scope.setContext('Peticion', {
        Url: _request.urlWithParams,
        Metodo: _request.method,
        // Descripcion que la propia app adjunta para decir que estaba haciendo el usuario
        Accion: _request.headers.get('Cl-Request-Error-Description'),
        CompanyId: _request.headers.get('cl-company-id'),
        HeadquarterId: _request.headers.get('cl-headquarter-id'),
        Cuerpo: SentryService.Payload(_request.body)
      });

      scope.setContext('Respuesta', {
        Estado: `${_response.status} ${_response.statusText}`,
        Cuerpo: SentryService.Payload(_response.error)
      });

      Sentry.captureException(new Error(`HTTP ${_response.status} ${_request.method} ${endpoint}`));
    });
  }

  /** Valor usado en los tags cuando el usuario aun no ha seleccionado compania o sede */
  private static readonly UNSET = 'sin-definir';

  /**
   * Nombres de campo cuyo contenido nunca debe salir de la aplicacion.
   *
   * El patron es deliberadamente especifico: un 'card' suelto ocultaria CardCode y
   * CardName, que son el socio de negocio y son justamente lo que hace falta ver para
   * diagnosticar un documento. Lo mismo con un 'pin' suelto, que ocultaria Shipping.
   */
  private static readonly SENSITIVE =
    /(password|passwd|passcode|contrasen|clave|token|authorization|secret|creditcard|cardnum|cardvalid|cardholder|numerotarjeta|tarjetanum|cvv|cvc)/i;

  /** Tope de caracteres por cuerpo adjunto, para no reventar el tamano del evento */
  private static readonly MAX_LENGTH = 4000;

  /**
   * Deja la ruta del endpoint sin host ni identificadores para que Sentry agrupe
   * todas las llamadas al mismo recurso en un solo issue.
   */
  private static Endpoint(_url: string): string {
    const path = _url.split('?')[0].replace(environment.apiUrl, '');

    return path.replace(/\/\d+(?=\/|$)/g, '/{id}');
  }

  /** Serializa un cuerpo de peticion o respuesta ocultando lo sensible y recortando el resto */
  private static Payload(_body: unknown): string | null {
    if (_body === null || _body === undefined) return null;

    try {
      if (typeof _body === 'string') return SentryService.Truncate(SentryService.RedactText(_body));

      if (_body instanceof FormData) return '[FormData]';

      if (_body instanceof Blob) return `[Blob de ${_body.size} bytes]`;

      return SentryService.Truncate(JSON.stringify(_body, (key, value) =>
        SentryService.SENSITIVE.test(key) ? '[oculto]' : value));
    } catch {
      return '[no serializable]';
    }
  }

  /** Oculta valores sensibles dentro de cuerpos de texto plano o urlencoded */
  private static RedactText(_text: string): string {
    return _text.replace(/(pass\w*|contrasen\w*|clave|token|secret)=[^&]*/gi, '$1=[oculto]');
  }

  private static Truncate(_text: string): string {
    return _text.length > SentryService.MAX_LENGTH
      ? `${_text.substring(0, SentryService.MAX_LENGTH)}... [recortado, ${_text.length} caracteres]`
      : _text;
  }
}
